import Swal from "sweetalert2";
import i18n from "@/i18n";
import { Ref, ref, watch } from "vue";
import { AxiosError } from "axios";

export const Toast = Swal.mixin({
  toast: true,
  position: "top-right",
  iconColor: "white",
  customClass: {
    popup: "colored-toast",
  },
  showConfirmButton: false,
  timer: 2000,
  timerProgressBar: true,
});

export function getErrorText(genericResponse, defaultErrorKey = undefined) {
  const errorCode = genericResponse.errorCode;

  let check = "ERROR_" + errorCode;
  let txt = i18n.global.t(check);
  if (txt != check) {
    return txt;
  }

  check = errorCode;
  txt = errorCode ? i18n.global.t(errorCode) : undefined;
  if (txt != check) {
    return txt;
  }

  if (defaultErrorKey) {
    return i18n.global.t(defaultErrorKey);
  }

  return genericResponse.error;
}

export function toastError(genericResponse, defaultErrorKey = undefined) {
  if (genericResponse instanceof AxiosError) {
    genericResponse = genericResponse.response.data;
  }
  const errorText = getErrorText(genericResponse, defaultErrorKey);
  Toast.fire({
    icon: "error",
    title: i18n.global.t("Error"),
    text: errorText,
  });
}

export function persistedRef<T>(key: string, defaultValue: T = undefined): [Ref<T>, () => void] {
  let value: string = null;
  const valRef: Ref<T> = ref(undefined);
  valRef.value = defaultValue;

  if (window["cefPersistedRef"]) {
    window["cefPersistedRef"].getVal(key).then((val) => {
      valRef.value = val ? JSON.parse(val) : defaultValue;
    });
  } else {
    value = localStorage.getItem(key);
    valRef.value = value !== null ? JSON.parse(value) : defaultValue;
  }

  watch(
    valRef,
    (val) => {
      if (val == defaultValue) {
        return;
      }
      if (window["cefPersistedRef"]) {
        window["cefPersistedRef"].setVal(key, JSON.stringify(val));
      } else {
        localStorage.setItem(key, JSON.stringify(val));
      }
    },
    { deep: true },
  );

  function clear() {
    if (window["cefPersistedRef"]) {
      window["cefPersistedRef"].clearVal(key);
    } else {
      localStorage.removeItem(key);
    }

    valRef.value = defaultValue;
  }

  return [valRef, clear];
}

export async function replaceLocalStorageWithPersistedRef() {
  window["localStorageCustom"] = window.localStorage;

  if (!window["cefPersistedRefBound"]) {
    const cefPersistedRef = await window["CefSharp"]?.BindObjectAsync("cefPersistedRef");

    if (!cefPersistedRef) {
      console.warn("replaceLocalStorageWithPersistedRef => cefPersistedRef not found");
      return;
    }
  }

  window["cefPersistedRefBound"] = true;

  if (!window["cefPersistedRef"]) {
    console.log("replaceLocalStorageWithPersistedRef => cefPersistedRef not found");
    return;
  }
  console.log("replaceLocalStorageWithPersistedRef => replacing localStorage with cefPersistedRef");

  window["localStorageCustom"] = {
    length: localStorage.length,
    key: async function (index) {
      return await localStorage.key(index);
    },
    setItem: async function (key, val) {
      await window["cefPersistedRef"].setVal(key, val);
    },
    getItem: async function (key) {
      return await window["cefPersistedRef"].getVal(key);
    },
    removeItem: async function (key) {
      await window["cefPersistedRef"].clearVal(key);
    },
    clear: async function () {
      await window["cefPersistedRef"].clearAll();
    },
  };
}

let collectingUrlParamChanges = false;
let urlParamChanges = {};

export async function setUrlParam(key, value) {
  const router = (await import("../router")).default;

  if (collectingUrlParamChanges) {
    urlParamChanges[key] = value;
    return;
  } else {
    collectingUrlParamChanges = true;
    urlParamChanges = {
      [key]: value,
    };
  }

  // delay for 500ms to collect all changes
  const promise = new Promise((resolve) => {
    setTimeout(() => {
      collectingUrlParamChanges = false;
      resolve(true);
    }, 100);
  });

  await promise;

  const urlParams = new URLSearchParams(window.location.search);

  for (const [key, value] of Object.entries(urlParamChanges)) {
    // ignore next line
    // @ts-ignore
    urlParams.set(key, value);
  }

  var paramsAsObject = Object.fromEntries(urlParams.entries());
  router.push({
    name: router.currentRoute.value.name,
    path: router.currentRoute.value.path,
    query: paramsAsObject,
    replace: true,
  });

  window.history.replaceState({}, "", `${window.location.pathname}?${urlParams}`);
}

function defaultFromUrl(val, type) {
  if (type == "number") {
    return parseFloat(val);
  }
  return val;
}

function defaultToUrl(val) {
  if (typeof val == "number") {
    return val.toString();
  }
  return val;
}

export function setUrlParamWithWatch(
  key,
  ref,
  loadAfter = false,
  toUrl = undefined,
  fromUrl = undefined,
  loadImmediately = false,
  setInitial = false,
) {
  if (!toUrl) {
    toUrl = (val) => defaultToUrl(val);
  }
  if (!fromUrl) {
    fromUrl = (val) => defaultFromUrl(val, typeof ref.value);
  }

  const urlParams = new URLSearchParams(window.location.search);
  const paramFromUrl = urlParams.get(key);

  if (paramFromUrl) {
    ref.value = fromUrl(paramFromUrl);
  }
  if (ref.value && setInitial) {
    setUrlParam(key, toUrl(ref.value));
  }

  watch(
    ref,
    (newVal) => {
      console.log("key", key, "newVal", newVal);
      setUrlParam(key, toUrl(newVal));
    },
    { deep: true },
  );

  if (typeof loadAfter == "function") {
    watch(ref, loadAfter, { immediate: loadImmediately });
  }
}

export function ucwords(input) {
  if (!input) {
    return undefined;
  }

  return (input + "").replace(/^([a-zığüşöç])|\s+([a-zığüşöç])/g, function ($1) {
    return $1.toUpperCase();
  });
}

export async function sleep(ms = 0) {
  return await new Promise((resolve) => setTimeout(resolve, ms));
}

export async function getCameras(type) {
  const devices = await navigator.mediaDevices.enumerateDevices();
  const videoInputDevices = devices.filter((device) => device.kind == "videoinput" && device.deviceId);

  if (videoInputDevices.length == 0 || !type) {
    return videoInputDevices;
  }

  let cameras = [];

  if (typeof type == "string") {
    type = type.toLowerCase();

    if (type == "front" || type == "selfie" || type == "user") {
      cameras = videoInputDevices.filter(
        (camera) => camera.label.toLowerCase().includes("front") || camera.label.toLowerCase().includes("selfie"),
      );
    } else if (type == "rear" || type == "back" || type == "environment") {
      cameras = videoInputDevices.filter(
        (camera) => camera.label.toLowerCase().includes("rear") || camera.label.toLowerCase().includes("back"),
      );
    } else {
      cameras = videoInputDevices.filter((camera) => camera.label.toLowerCase().includes(type));
    }
  } else if (Array.isArray(type) && type.length > 0) {
    cameras = videoInputDevices.filter((camera) => type.some((t) => camera.label.toLowerCase().includes(t.toLowerCase())));
  } else if (typeof type == "object") {
    cameras = videoInputDevices.filter((camera) =>
      Object.values(type).some((t) => camera.label.toLowerCase().includes((t as string).toLowerCase())),
    );
  } else if (typeof type == "function") {
    cameras = videoInputDevices.filter(type);
  } else {
    cameras = videoInputDevices;
  }

  return cameras;
}

export async function copyToClipboard(text) {
  if (navigator?.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      // Fallback
      console.error(error);
      return false;
    }
  } else {
    // Fallback
    return false;
  }
}

export function prettifyInlineJsonString(jsonStr) {
  if (typeof jsonStr != "string") {
    return jsonStr;
  }

  return jsonStr
    .replace(/,/g, ", ")
    .replace(/:/g, ": ")
    .replace(/{/g, "{ ")
    .replace(/}/g, " }")
    .replace(/\[/g, "[ ")
    .replace(/\]/g, " ]");
}

export function formatDateTimeForFileNaming(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based, so add 1
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0"); // 24-hour format
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}${month}${day}-${hours}${minutes}${seconds}`;
}

export function dateTimeFormat(dbDate) {
  // if date is 1970 or null return empty string
  if (dbDate == null || dbDate == "1970-01-01T00:00:00Z") {
    return "";
  }
  // date can be a string or a Date object
  // check 1970 for date object
  if (dbDate instanceof Date) {
    if (dbDate.getFullYear() == 1970) {
      return "";
    }
  }

  return new Date(dbDate).toLocaleString(window.CONFIG.DATE_LOCALE);
}

export function userMediaErrorT(error){
  switch (error.name) {
    case "NotAllowedError":
      return i18n.global.t("UserMediaNotAllowedError");
    case "NotFoundError":
      return i18n.global.t("UserMediaNotFoundError");
    case "NotReadableError":
      return i18n.global.t("UserMediaNotReadableError");
    case "OverconstrainedError":
      return i18n.global.t("UserMediaOverconstrainedError");
    case "SecurityError":
      return i18n.global.t("UserMediaSecurityError");
    case "TypeError":
      return i18n.global.t("UserMediaTypeError");
    default:
      return `${i18n.global.t("UserMediaUnknownError")}: ${error.message}`;
  }
}
